import * as React from 'react';
import {
    AdminUserSearchResult,
    AdminUserSearchResultExtended,
    DomainWithFilespaces
} from 'fs-api-client';
import { createDefaultApi } from '../../../webportal/utils/createApi';
import { User } from '../common/User';
import { ErrorRenderer } from '../common/ErrorRenderer';
import { FETCHING_MESSAGE } from '../../utils/constants';
import { renderWaitingForSuccessElement } from '../../../webportal/components/common/Icons';
import { sortFilespaces } from '../../utils/utilFunctions';

interface UserSearchResultsProps {
    userSearchResults: AdminUserSearchResult[];
    securityScopes: string [];
    fetchUsers: (name?: string) => Promise<void>;
}

interface UserSearchResultState {
    selectedUserId: string;
    domains: DomainWithFilespaces[];
    userExtra?: AdminUserSearchResultExtended;
    fetching: boolean;
    currentUser: AdminUserSearchResult | undefined;
    isInitial: boolean;
    hasError: boolean;
    isUserCardClicked: boolean;
    errorMessage: string;
}

export class UserSearchResults extends React.Component<UserSearchResultsProps, UserSearchResultState> {
    constructor(props: UserSearchResultsProps) {
        super(props);
        this.state = {
            selectedUserId: '',
            domains: [],
            fetching: false,
            currentUser: undefined,
            isInitial: true,
            hasError: false,
            isUserCardClicked: false,
            errorMessage: ''
        };

        this.renderUserSearchResultCards = this.renderUserSearchResultCards.bind(this);
        this.renderUserCards = this.renderUserCards.bind(this);
        this.onUserCardClicked = this.onUserCardClicked.bind(this);
        this.removeErrorComponent = this.removeErrorComponent.bind(this);
    }

    async componentDidMount() {
        await this.setInitialUser();
    }

    render() {
        return (
            <React.Fragment>
                <nav className="col-3 leftSpaceOnly">
                    {this.renderUserSearchResultCards()}
                </nav>
                <div className="col-8 content">
                    {this.state.fetching && renderWaitingForSuccessElement(FETCHING_MESSAGE)}
                    {this.state.hasError && <ErrorRenderer errorMessage={this.state.errorMessage}/>}
                    {this.state.isInitial && this.state.currentUser && this.renderUserDetails(this.state.currentUser)}
                    {this.state.isUserCardClicked && this.state.currentUser && this.renderUserDetails(this.state.currentUser)}
                </div>
            </React.Fragment>
        );

    }

    private renderUserSearchResultCards(): JSX.Element {
        return (
            <ul>
                {this.props.userSearchResults.map((userResult: AdminUserSearchResult) => this.renderUserCards(userResult))}
            </ul>
        );
    }

    private async setInitialUser(): Promise<void> {
        const api = await createDefaultApi();
        const userId = this.props.userSearchResults[0].id;
        const userAdditionalInfo = await api.getUserAdditionalInformationById(userId);
        const userExtra = JSON.parse(JSON.stringify(userAdditionalInfo.response)).body;

        userExtra.givenName = userExtra.firstName;
        userExtra.familyName = userExtra.lastName;

        this.setState(
            {
                currentUser: this.props.userSearchResults[0],
                userExtra: userExtra,
                selectedUserId: this.props.userSearchResults[0].id,
            },
            () => {
                this.onUserCardClicked(this.props.userSearchResults[0]);
            });
    }

    private renderUserCards(userResult: AdminUserSearchResultExtended): JSX.Element | null {
        if (userResult && this.state.currentUser) {
            return (
                <React.Fragment key={userResult.id}>
                    <li
                        onClick={async () => {
                            await this.onUserCardClicked(userResult);
                        }}
                    >
                        {this.state.selectedUserId === userResult.id &&
                            <span>
                        {userResult.email}
                                {this.renderUserCardDetails(userResult)}
                    </span>}
                        {this.state.selectedUserId !== userResult.id &&
                            <a href="javascript:void(0)">
                                {userResult.email}
                                {this.renderUserCardDetails(userResult)}

                            </a>}
                    </li>
                </React.Fragment>
            );
        }
        return null;
    }

    private renderUserDetails(userResult: AdminUserSearchResult): JSX.Element | null {
        if (this.state.domains && userResult) {
            return (
                <User
                    user={userResult}
                    domain={this.state.domains[0]}
                    userExtra={this.state.userExtra}
                    securityScopes={this.props.securityScopes}
                    onSuccess={this.props.fetchUsers}
                />);
        }
        return null;
    }

    private renderUserCardDetails(user: AdminUserSearchResultExtended): JSX.Element | null {
        return (
            <React.Fragment key={user.id}>
                <ul>
                    {user.isSuspended && <li className="warning" title="User status">Suspended</li>}
                    {!user.isSuspended && <li className="active" title="User status">Active</li>}
                    {user.identityProviders && <li title="Provider">{user.identityProviders}</li>}
                    {user.companyName && <li title="Company name">{user.companyName}</li>}
                </ul>
            </React.Fragment>
        );
    }

    private async onUserCardClicked(user: AdminUserSearchResultExtended): Promise<void> {
        this.setState({ fetching: true, isUserCardClicked: false });
        this.fetchFilespaces(user.id);
        const api = await createDefaultApi();
        const userAdditionalInfo = await api.getUserAdditionalInformationById(user.id);
        const userExtended = JSON.parse(JSON.stringify(userAdditionalInfo.response)).body;
        userExtended.givenName = userExtended.firstName;
        userExtended.familyName = userExtended.lastName;

        this.setState({
                          isInitial: false,
                          isUserCardClicked: true,
                          selectedUserId: user.id,
                          currentUser: user,
                          userExtra: userExtended,
                          fetching: false
                      });
    }

    private async fetchFilespaces(userId: string): Promise<void> {
        this.setState({ fetching: true });
        const api = await createDefaultApi();
        try {
            const domains = await api.getDomainsByUserId(userId);
            sortFilespaces(domains.body);
            if (!domains.body) {
                this.setState({ hasError: true, errorMessage: 'No filespaces were found.' });
                this.removeErrorComponent();
                return;
            }
            this.setState({
                              domains: domains.body,
                              fetching: false
                          });
        } catch (e) {
            this.setState(
                {
                    hasError: true,
                    errorMessage: e.message,
                    fetching: false
                });
            this.removeErrorComponent();
        }
    }

    private removeErrorComponent(): void {
        setTimeout(
            () => {
                this.setState({ hasError: false, errorMessage: '' });
            },
            5000);
    }
}



// WEBPACK FOOTER //
// ./src/backoffice/components/user/UserSearchResults.tsx